import { Input, Stack, VisuallyHidden } from '@chakra-ui/react'
import type { FC, ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { Field } from '@app/components/ui/field'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import type {
  ChakraAsyncCreatableSelectProps,
  ReactSelectAutocompleteValue
} from '@app/shared/autocomplete/chakraAutocompletes'
import ContributorsAutocomplete from '@app/shared/autocomplete/contributors/contributorsAutocomplete'
import { useStore } from '@app/store'
import type { FormErrors, DomainObject } from '@app/types'

const valueTransformer = (contributors) => JSON.stringify(contributors.map((contributor) => contributor.name))

export interface Props
  extends Partial<Omit<ChakraAsyncCreatableSelectProps<ReactSelectAutocompleteValue[]>, 'defaultValue'>> {
  label?: ReactNode | string
  domainObject: DomainObject
  errors?: FormErrors
  helperText?: ReactNode
}

const ContributorsInput: FC<Props> = ({ domainObject, name, label, helperText, errors = {}, ...rest }) => {
  // console.log('top', domainObject?.[name])
  const [contributors, setContributors] = useState(domainObject?.[name]?.map((i) => i.contributor) || [])
  const { user } = useStoreCurrentUser()
  const updateObject = useStore.use.updateObject()

  useEffect(() => {
    if (domainObject?.[name] !== contributors) {
      setContributors(domainObject?.[name])
    }
  }, [domainObject, contributors, name])

  const handleChange = (value) => {
    // console.log('value', value)
    const newContributors = value.map((newContributor) => ({
      id: newContributor.value,
      sourceName: newContributor.sourceName,
      name: newContributor.label,
      email: newContributor.email,
      value: newContributor.value
    }))

    setContributors(newContributors)
    // @ts-expect-error - Unclear why this is failing
    updateObject({ [domainObject.classType]: { id: domainObject.id, [name]: newContributors } })

    return null
  }

  return (
    <Field label={label} errorText={errors[name]?.message} helperText={helperText} id={name} invalid={!!errors[name]}>
      <VisuallyHidden asChild>
        <Input id={name} name={name} readOnly value={valueTransformer(contributors)} />
      </VisuallyHidden>
      <Stack w="100%">
        <ContributorsAutocomplete
          key={valueTransformer(contributors)}
          id={`${name}-autocomplete`}
          onChange={handleChange}
          defaultValue={contributors}
          readOnly={!['admin', 'editor'].includes(user?.role)}
          chakraStyles={{
            inputContainer: (provided, _state) => ({
              ...provided,
              cursor: 'pointer'
            })
          }}
          components={{
            DropdownIndicator: () => null
          }}
          {...rest}
        />
      </Stack>
    </Field>
  )
}

export default ContributorsInput
